/**
 * Positive eBay feedback for mossmatrix — scraped snapshot, not invented.
 *
 * Source: https://www.ebay.co.uk/fdbk/feedback_profile/mossmatrix
 * Profile table shows 254 positive in last 12 months; this file stores every
 * comment row we could paginate from the seller feedback list (see data JSON).
 *
 * @typedef {Object} EbayReview
 * @property {string} id
 * @property {string} [feedbackId]
 * @property {string} buyerLabel
 * @property {string} productName
 * @property {string} [itemId]
 * @property {string} comment
 * @property {'positive'} rating
 * @property {string} [dateLabel]
 * @property {boolean} verifiedPurchase
 * @property {string} sourceLabel
 * @property {boolean} [featured]
 * @property {boolean} [automated]
 * @property {'substantive'|'short'|'automated'} [tier]
 */

import data from "./ebayPositiveReviews.data.json";

export const EBAY_FEEDBACK_SOURCE_URL =
  data.sourceUrl || "https://www.ebay.co.uk/fdbk/feedback_profile/mossmatrix";

/** Official positive count from the eBay profile ratings table (last 12 months). */
export const EBAY_POSITIVE_COUNT =
  data.positiveCountDisplayed ||
  data.positiveLast12Months ||
  254;

export const EBAY_POSITIVE_PCT_LAST_12 =
  data.positiveFeedbackPctLast12 ?? 100;

export const EBAY_REVIEWS_NOTE =
  "From eBay — positive feedback left for Moss Matrix on eBay. Buyer words shown as quotes; not Google or website reviews.";

/**
 * Aggregate detailed seller ratings from the eBay profile (not per-review scores).
 * UI labels are friendly; ebayLabel is the exact DSR name from the profile.
 * @type {{ id: string, label: string, ebayLabel: string, scoreOutOf5: number }[]}
 */
export const EBAY_SELLER_CRITERIA = (() => {
  const raw = Array.isArray(data.detailedSellerRatings)
    ? data.detailedSellerRatings
    : [];
  const by = Object.fromEntries(raw.map((r) => [r.label, r.scoreOutOf5]));

  return [
    {
      id: "quality",
      label: "Item as described",
      ebayLabel: "Accurate description",
      scoreOutOf5: by["Accurate description"] ?? 5,
    },
    {
      id: "packaging",
      label: "Packaging & postage",
      ebayLabel: "Reasonable postage cost",
      scoreOutOf5: by["Reasonable postage cost"] ?? 5,
    },
    {
      id: "delivery",
      label: "Delivery time",
      ebayLabel: "Delivery time",
      scoreOutOf5: by["Delivery time"] ?? 5,
    },
    {
      id: "communication",
      label: "Communication",
      ebayLabel: "Communication",
      scoreOutOf5: by["Communication"] ?? 5,
    },
  ];
})();

const AUTOMATED_RE =
  /^(Order delivered on time with no issues|Quick and easy transaction\.?)\s*$/i;

function classifyTier(comment, buyerLabel) {
  const c = (comment || "").trim();
  if (
    AUTOMATED_RE.test(c) ||
    /automated Feedback/i.test(buyerLabel || "")
  ) {
    return "automated";
  }
  if (c.length >= 48) return "substantive";
  return "short";
}

function scoreForFeature(r) {
  // Higher = better for featured carousel
  let s = 0;
  if (r.tier === "substantive") s += 100;
  else if (r.tier === "short") s += 40;
  else s -= 50;
  s += Math.min(80, (r.comment || "").length / 4);
  if (/quality|taste|packag|recommend|seller|communicat|delivery|service|value/i.test(r.comment || "")) {
    s += 25;
  }
  if (r.verifiedPurchase) s += 8;
  return s;
}

/** @type {EbayReview[]} */
export const EBAY_POSITIVE_REVIEWS = (Array.isArray(data.reviews) ? data.reviews : [])
  .filter((r) => r && r.rating === "positive" && (r.comment || "").trim())
  .map((r) => {
    const tier = classifyTier(r.comment, r.buyerLabel);
    return {
      id: r.id,
      feedbackId: r.feedbackId,
      buyerLabel: r.buyerLabel || "eBay buyer",
      productName: r.productName || "Moss Matrix purchase",
      itemId: r.itemId,
      comment: String(r.comment).trim(),
      rating: "positive",
      dateLabel: r.dateLabel,
      verifiedPurchase: Boolean(r.verifiedPurchase),
      sourceLabel: r.sourceLabel || "From eBay",
      automated: tier === "automated",
      tier,
      featured: false,
    };
  })
  .sort((a, b) => scoreForFeature(b) - scoreForFeature(a))
  .map((r, i) => ({
    ...r,
    // Curate top substantive quotes for carousel / home teaser
    featured: r.tier === "substantive" && i < 12,
  }));

/** Positive eBay reviews only. */
export function getEbayPositiveReviews({ includeAutomated = true } = {}) {
  const all = EBAY_POSITIVE_REVIEWS;
  if (includeAutomated) return all;
  return all.filter((r) => r.tier !== "automated");
}

/** Substantive first, then short, automated last — for progressive wall. */
export function getEbayReviewsForWall() {
  const order = { substantive: 0, short: 1, automated: 2 };
  return [...getEbayPositiveReviews({ includeAutomated: true })].sort((a, b) => {
    const t = (order[a.tier] ?? 9) - (order[b.tier] ?? 9);
    if (t !== 0) return t;
    return (b.comment?.length || 0) - (a.comment?.length || 0);
  });
}

/** Homepage / teaser: featured substantive quotes. */
export function getFeaturedEbayReviews(max = 8) {
  const featured = EBAY_POSITIVE_REVIEWS.filter((r) => r.featured);
  if (featured.length >= max) return featured.slice(0, max);
  const rest = EBAY_POSITIVE_REVIEWS.filter(
    (r) => !r.featured && r.tier !== "automated"
  );
  return [...featured, ...rest].slice(0, max);
}

export function ebayBuyerInitials(buyerLabel) {
  if (!buyerLabel || buyerLabel === "eBay buyer") return "EB";
  if (/automated/i.test(buyerLabel)) return "EB";
  const letters = buyerLabel.replace(/[^a-zA-Z0-9]/g, "");
  if (!letters) return "EB";
  if (letters.length === 1) return letters[0].toUpperCase();
  return (letters[0] + letters[letters.length - 1]).toUpperCase();
}

/**
 * Map eBay review → MarketplaceFeedbackItem shape for ReviewCard.
 * Positive feedback displays as 5 stars (eBay positive = thumbs up).
 * @param {EbayReview} r
 */
export function ebayReviewToMarketplaceItem(r) {
  return {
    id: r.id,
    customerLabel: r.buyerLabel,
    productName: r.productName,
    rating: 5,
    comment: r.comment,
    sourceLabel: r.sourceLabel || "From eBay",
    dateLabel: r.dateLabel,
    verifiedLabel: r.verifiedPurchase ? "Verified purchase" : undefined,
    featured: Boolean(r.featured),
    approved: true,
    tags: r.tier === "automated" ? ["eBay", "Positive", "Delivery"] : ["eBay", "Positive"],
    tier: r.tier,
  };
}

export function getIngestedEbayReviewCount() {
  return EBAY_POSITIVE_REVIEWS.length;
}
